import {Palette, QuestionType, User} from 'hubbl-shared';
import React, {useMemo} from 'react';
import styled from 'styled-components';
import {EventDTO} from '../../models/event/EventDTO';
import {
  PaddingBottom,
  TextBodySmall,
  TextHeading3,
  TextHeading5,
} from '../index';
import {useI18n} from '../../hooks';

interface AttendeeQuestionsProps {
  event: EventDTO;
  attendee: User;
  textColor: Palette.white | Palette.almostBlack | undefined;
}

const QuestionContainer = styled.div`
  margin-bottom: 12px;
`;

export const AttendeeQuestions: React.FC<AttendeeQuestionsProps> = ({
  event,
  attendee,
  textColor,
}) => {
  const t = useI18n();
  // Get attendee's responses to pre-attending questions
  const attendeeResponses = useMemo(() => {
    if (!event || !attendee || !event.questions || !event.questionResponses) {
      return [];
    }

    return event.questions
      .map((question: any) => {
        const responses = event.questionResponses[question.id] || [];
        const attendeeResponse = responses.find(
          (response: any) => response.user.id === attendee.id,
        );

        if (!attendeeResponse) {
          return null;
        }

        let responseText = '';
        if (question.type === QuestionType.FreeText) {
          responseText = attendeeResponse.freeTextResponse || '';
        } else {
          const selectedOptions = attendeeResponse.selectedOptions || [];
          const optionTexts = selectedOptions
            .map((optionId: any) => {
              const option = question.options?.find(
                (opt: any) => opt.id === optionId,
              );
              return option?.text || '';
            })
            .filter((text: string) => text)
            .join(', ');

          responseText = optionTexts;

          // Add free text response if available
          if (attendeeResponse.freeTextResponse) {
            responseText += ` (${attendeeResponse.freeTextResponse})`;
          }
        }

        return {
          question,
          response: responseText,
        };
      })
      .filter(Boolean);
  }, [event, attendee]);

  if (attendeeResponses.length === 0) {
    return null;
  }

  return (
    <>
      <PaddingBottom amount={16} />
      <TextHeading3 text={t.questions.myResponses} color={textColor} />
      {attendeeResponses.map((item: any, index: number) => (
        <QuestionContainer key={item.question.id}>
          <TextHeading5
            text={`${index + 1}. ${item.question.questionText}`}
            color={textColor}
          />
          <TextBodySmall
            text={item.response || t.questions.noResponse}
            color={textColor}
          />
        </QuestionContainer>
      ))}
      {attendeeResponses.length > 0 && <PaddingBottom amount={16} />}
    </>
  );
};
